import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../../services/api";
import { tx, useMvLanguage } from "../../utils/mvText";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PartnerData {
  full_name: string;
  date_of_birth: string;
  time_of_birth: string;
  place_of_birth: string;
  gender_expression: string;
  current_city: string;
}

interface FormData {
  partner_a: PartnerData;
  partner_b: PartnerData;
  relationship_stage: string;
  current_feeling: string;
  time_together: string;
  broken_up_before: string;
  initiated_by: string;
  family_support: string;
  religion_culture: string[];
  language_background: string;
  plan_to_marry: string;
  preferred_day_type: string[];
  wedding_style: string;
  relationship_feels_like: string;
  biggest_challenge: string;
  strongest_point: string;
  venue_type: string;
  preferred_colors: string[];
  colors_to_avoid: string[];
  wedding_style_preference: string;
  need_gift_list: boolean;
  need_checklist: boolean;
  need_vastu_direction: boolean;
}

const emptyPartner = (): PartnerData => ({
  full_name: "",
  date_of_birth: "",
  time_of_birth: "",
  place_of_birth: "",
  gender_expression: "",
  current_city: "",
});

// Converts a displayed DD/MM/YYYY value to the ISO YYYY-MM-DD format the backend validator requires.
const dobToIso = (value: string): string => {
  const parts = value.split("/");
  if (parts.length !== 3) return value;
  const [day, month, year] = parts;
  if (!day || !month || !year) return value;
  return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

// Converts a stored ISO YYYY-MM-DD value back to the displayed DD/MM/YYYY format.
const isoToDob = (value: string): string => {
  const parts = value.split("-");
  if (parts.length !== 3) return value;
  const [year, month, day] = parts;
  if (!day || !month || !year) return value;
  return `${day}/${month}/${year}`;
};

const initialForm = (): FormData => ({
  partner_a: emptyPartner(),
  partner_b: emptyPartner(),
  relationship_stage: "",
  current_feeling: "",
  time_together: "",
  broken_up_before: "",
  initiated_by: "",
  family_support: "",
  religion_culture: [],
  language_background: "",
  plan_to_marry: "",
  preferred_day_type: [],
  wedding_style: "",
  relationship_feels_like: "",
  biggest_challenge: "",
  strongest_point: "",
  venue_type: "",
  preferred_colors: [],
  colors_to_avoid: [],
  wedding_style_preference: "",
  need_gift_list: false,
  need_checklist: false,
  need_vastu_direction: false,
});

// ─── Shared UI atoms ──────────────────────────────────────────────────────────

const CardOption: React.FC<{
  label: string;
  desc?: string;
  selected: boolean;
  onClick: () => void;
}> = ({ label, desc, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="p-3 rounded-2xl border text-left transition-all duration-200"
    style={
      selected
        ? {
            background: "#221a29",
            borderColor: "#C084FC",
            boxShadow: "0 1px 4px #C084FC22",
          }
        : { background: "#1a131f", borderColor: "rgba(255,255,255,0.12)" }
    }
  >
    <p
      className="text-xs font-semibold"
      style={{ color: selected ? "#d9ccff" : "#f7f3fa" }}
    >
      {tx(label)}
    </p>
    {desc && (
      <p className="text-[10px] mt-0.5" style={{ color: "#a89bb5" }}>
        {tx(desc)}
      </p>
    )}
  </button>
);

const PillOption: React.FC<{
  label: string;
  selected: boolean;
  onClick: () => void;
}> = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="px-4 py-2 rounded-full text-xs font-semibold tracking-wide border transition-all duration-200"
    style={
      selected
        ? {
            background: "#a78bff",
            borderColor: "#C084FC",
            color: "#0f0a14",
            boxShadow: "0 2px 6px #C084FC33",
          }
        : { background: "#1a131f", borderColor: "rgba(255,255,255,0.12)", color: "#c8bfd1" }
    }
  >
    {tx(label)}
  </button>
);

const ToggleSwitch: React.FC<{
  label: string;
  description?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, description, value, onChange }) => (
  <div
    onClick={() => onChange(!value)}
    className="flex items-center justify-between p-4 rounded-2xl border cursor-pointer transition-all duration-200"
    style={
      value
        ? { background: "#221a29", borderColor: "#C084FC" }
        : { background: "#1a131f", borderColor: "rgba(255,255,255,0.12)" }
    }
  >
    <div>
      <p
        className="text-xs font-semibold"
        style={{ color: value ? "#d9ccff" : "#f7f3fa" }}
      >
        {tx(label)}
      </p>
      {description && (
        <p className="text-[10px] mt-0.5" style={{ color: "#a89bb5" }}>
          {tx(description)}
        </p>
      )}
    </div>
    <div
      className="w-11 h-6 rounded-full transition-all duration-300 relative flex-shrink-0"
      style={{ background: value ? "#C084FC" : "rgba(255,255,255,0.12)" }}
    >
      <span
        className={`absolute top-1 w-4 h-4 rounded-full bg-[#1a131f] shadow transition-all duration-300 ${value ? "left-6" : "left-1"}`}
      />
    </div>
  </div>
);

const InputField: React.FC<{
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}> = ({ label, type = "text", value, onChange, placeholder, required }) => (
  <div>
    <label
      className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-1.5"
      style={{ color: "#c8bfd1" }}
    >
      {tx(label)}
      {required && (
        <span className="ml-1" style={{ color: "#C084FC" }}>
          *
        </span>
      )}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder ? tx(placeholder) : placeholder}
      className="w-full rounded-xl px-4 py-2.5 text-sm transition-all focus:outline-none"
      style={{
        background: "#1a131f",
        border: "1px solid rgba(255,255,255,0.12)",
        color: "#f7f3fa",
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = "#C084FC";
        e.currentTarget.style.boxShadow = "0 0 0 2px #C084FC18";
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
        e.currentTarget.style.boxShadow = "none";
      }}
    />
  </div>
);

// ─── Date of Birth formatting helpers (mirrors EnergyMatchDesign styling) ─────

const formatDateValue = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  const limitedDigits = digits.slice(0, 8);
  let formatted = "";
  for (let i = 0; i < limitedDigits.length; i++) {
    if (i === 2 || i === 4) {
      formatted += "/";
    }
    formatted += limitedDigits[i];
  }
  return formatted;
};

const validateDateValue = (value: string): string => {
  if (!value) return value;
  const parts = value.split("/");
  if (parts.length === 3) {
    let day = parseInt(parts[0], 10);
    let month = parseInt(parts[1], 10);
    let year = parseInt(parts[2], 10);
    if (isNaN(day)) day = 1;
    if (day < 1) day = 1;
    if (day > 31) day = 31;
    if (isNaN(month)) month = 1;
    if (month < 1) month = 1;
    if (month > 12) month = 12;
    if (isNaN(year)) year = new Date().getFullYear();
    if (year < 1900) year = 1900;
    if (year > 2099) year = 2099;
    return `${day.toString().padStart(2, "0")}/${month.toString().padStart(2, "0")}/${year}`;
  }
  return value;
};

const DateOfBirthField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}> = ({ value, onChange, required }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = input.value;
    const cursorPosition = input.selectionStart || 0;
    const oldFormatted = rawValue;
    const newFormatted = formatDateValue(rawValue);
    onChange(newFormatted);
    setTimeout(() => {
      if (input === document.activeElement) {
        let newCursorPos = cursorPosition;
        if (cursorPosition === 3 && newFormatted.length > oldFormatted.length) {
          newCursorPos = 4;
        } else if (
          cursorPosition === 6 &&
          newFormatted.length > oldFormatted.length
        ) {
          newCursorPos = 7;
        }
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  return (
    <div>
      <label
        className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-1.5"
        style={{ color: "#c8bfd1" }}
      >
        {tx("Date of Birth")}
        {required && (
          <span className="ml-1" style={{ color: "#C084FC" }}>
            *
          </span>
        )}
      </label>
      <input
        type="text"
        inputMode="numeric"
        placeholder={tx("DD / MM / YYYY")}
        value={value}
        onChange={handleChange}
        onBlur={(e) => onChange(validateDateValue(e.target.value))}
        className="w-full rounded-xl px-4 py-2.5 text-sm transition-all focus:outline-none"
        style={{
          background: "#1a131f",
          border: "1px solid rgba(255,255,255,0.12)",
          color: "#f7f3fa",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = "#C084FC";
          e.currentTarget.style.boxShadow = "0 0 0 2px #C084FC18";
        }}
      />
    </div>
  );
};

// ─── Time of Birth formatting helpers (mirrors EnergyMatchDesign styling) ─────

const formatTimeValue = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  const limitedDigits = digits.slice(0, 4);
  let formatted = "";
  for (let i = 0; i < limitedDigits.length; i++) {
    if (i === 2) {
      formatted += ":";
    }
    formatted += limitedDigits[i];
  }
  return formatted;
};

const validateTimeValue = (value: string): string => {
  if (!value) return value;
  const parts = value.split(":");
  if (parts.length === 2) {
    let hour = parseInt(parts[0], 10);
    let minute = parseInt(parts[1], 10);
    if (isNaN(hour)) hour = 0;
    if (hour < 0) hour = 0;
    if (hour > 23) hour = 23;
    if (isNaN(minute)) minute = 0;
    if (minute < 0) minute = 0;
    if (minute > 59) minute = 59;
    return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
  }
  return value;
};

const TimeOfBirthField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}> = ({ value, onChange, required }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = input.value;
    const cursorPosition = input.selectionStart || 0;
    const oldFormatted = rawValue;
    const newFormatted = formatTimeValue(rawValue);
    onChange(newFormatted);
    setTimeout(() => {
      if (input === document.activeElement) {
        let newCursorPos = cursorPosition;
        if (cursorPosition === 3 && newFormatted.length > oldFormatted.length) {
          newCursorPos = 4;
        }
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  return (
    <div>
      <label
        className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-1.5"
        style={{ color: "#c8bfd1" }}
      >
        {tx("Time of Birth")}
        {required && (
          <span className="ml-1" style={{ color: "#C084FC" }}>
            *
          </span>
        )}
      </label>
      <input
        type="text"
        inputMode="numeric"
        placeholder={tx("HH : MM")}
        value={value}
        onChange={handleChange}
        onBlur={(e) => onChange(validateTimeValue(e.target.value))}
        className="w-full rounded-xl px-4 py-2.5 text-sm transition-all focus:outline-none"
        style={{
          background: "#1a131f",
          border: "1px solid rgba(255,255,255,0.12)",
          color: "#f7f3fa",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = "#C084FC";
          e.currentTarget.style.boxShadow = "0 0 0 2px #C084FC18";
        }}
      />
    </div>
  );
};

// ─── Loading Screen ───────────────────────────────────────────────────────────

const LOADING_STEPS = [
  { label: "Reading birth charts...", icon: "✦" },
  { label: "Calculating Nakshatra compatibility...", icon: "◈" },
  { label: "Analysing Guna Milan (36 points)...", icon: "◉" },
  { label: "Consulting Vedic Muhurat windows...", icon: "☽" },
  { label: "Generating AI Marriage Verdict...", icon: "✧" },
  { label: "Preparing your cosmic report...", icon: "◇" },
];

const LoadingScreen: React.FC = () => {
  useMvLanguage();
  const [activeIdx, setActiveIdx] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setActiveIdx((prev) => {
        const next = prev + 1;
        if (next >= LOADING_STEPS.length) {
          clearInterval(timerRef.current!);
          return prev;
        }
        return next;
      });
    }, 2200);
    return () => clearInterval(timerRef.current!);
  }, []);

  const currentStep = LOADING_STEPS[activeIdx];

  return (
    <div className="min-h-screen w-full bg-[#0f0a14] [color-scheme:dark] flex flex-col items-center justify-center px-6 py-16 relative overflow-hidden">
      {/* Background orbs — Indian warmth + purple */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-24 -left-24 w-72 h-72 rounded-full blur-3xl"
          style={{
            background: "radial-gradient(circle, #C084FC 0%, transparent 70%)",
            opacity: 0.07,
          }}
        />
        <div
          className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full blur-3xl"
          style={{
            background: "radial-gradient(circle, #C084FC 0%, transparent 70%)",
            opacity: 0.1,
          }}
        />
        <div
          className="absolute top-1/3 right-10 w-48 h-48 rounded-full blur-2xl"
          style={{
            background: "radial-gradient(circle, #C084FC 0%, transparent 70%)",
            opacity: 0.06,
          }}
        />
      </div>

      {/* Mandala ring */}
      <div className="relative mb-10">
        <svg
          width="128"
          height="128"
          viewBox="0 0 128 128"
          className="animate-spin"
          style={{ animationDuration: "9s" }}
        >
          <circle
            cx="64"
            cy="64"
            r="56"
            fill="none"
            stroke="#C084FC"
            strokeWidth="1"
            strokeDasharray="5 7"
            opacity="0.35"
          />
          <circle
            cx="64"
            cy="64"
            r="42"
            fill="none"
            stroke="#C084FC"
            strokeWidth="1"
            strokeDasharray="2 9"
            opacity="0.3"
          />
          {[0, 40, 80, 120, 160, 200, 240, 280, 320].map((deg, i) => {
            const rad = (deg * Math.PI) / 180;
            return (
              <circle
                key={i}
                cx={64 + 56 * Math.cos(rad)}
                cy={64 + 56 * Math.sin(rad)}
                r="2.5"
                fill="#C084FC"
                opacity="0.45"
              />
            );
          })}
        </svg>

        {/* Counter-rotating Star of David (Shatkona) */}
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ animation: "spin 6s linear infinite reverse" }}
        >
          <svg width="64" height="64" viewBox="0 0 64 64">
            <polygon
              points="32,6 58,50 6,50"
              fill="none"
              stroke="#C084FC"
              strokeWidth="1.5"
              opacity="0.5"
            />
            <polygon
              points="32,58 6,14 58,14"
              fill="none"
              stroke="#C084FC"
              strokeWidth="1.5"
              opacity="0.4"
            />
          </svg>
        </div>

        {/* Center Om / moon symbol */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C084FC]/20 to-[#6D4C92]/20 border border-[#C084FC]/30 flex items-center justify-center">
            <span className="text-xl text-[#d9ccff]">☽</span>
          </div>
        </div>
      </div>

      {/* Title */}
      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xl sm:text-2xl font-light tracking-wide mb-1 text-center"
        style={{ color: "#c8bfd1" }}
      >
        {tx("Astria is reading your cosmic path")}
      </motion.h2>
      <p
        className="text-xs tracking-[0.18em] uppercase mb-10 text-center"
        style={{ color: "#a89bb5" }}
      >
        {tx("Nakshatra · Guna Milan · Vivah Muhurat")}
      </p>

      {/* Single-message step — one at a time, replaces in-place */}
      <div className="w-full max-w-xs min-h-[52px] flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIdx}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-[#221a29] border border-[#C084FC]/30 shadow-sm"
          >
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#C084FC] to-[#6D4C92]">
              <span className="text-white text-sm">{currentStep.icon}</span>
            </div>
            <span className="text-sm font-medium" style={{ color: "#c8bfd1" }}>
              {tx(currentStep.label)}
            </span>
            {/* Animated dots */}
            <div className="ml-1 flex gap-0.5">
              {[0, 1, 2].map((d) => (
                <div
                  key={d}
                  className="w-1 h-1 rounded-full"
                  style={{
                    background: "#C084FC",
                    animation: `bounce 1s ease-in-out ${d * 0.18}s infinite`,
                  }}
                />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Progress dots */}
      <div className="flex gap-2 mt-6">
        {LOADING_STEPS.map((_, i) => (
          <div
            key={i}
            className="rounded-full transition-all duration-500"
            style={{
              width: i === activeIdx ? 20 : 6,
              height: 6,
              background:
                i === activeIdx
                  ? "#C084FC"
                  : i < activeIdx
                    ? "#C084FC"
                    : "rgba(255,255,255,0.12)",
            }}
          />
        ))}
      </div>

      {/* Divider */}
      <div
        className="mt-10 flex items-center gap-3 text-[10px] tracking-[0.2em] uppercase"
        style={{ color: "#a89bb5" }}
      >
        <span
          className="inline-block border-t w-10"
          style={{ borderColor: "#C084FC40" }}
        />
        {tx("mangal · nakshatra · dasha · muhurat")}
        <span
          className="inline-block border-t w-10"
          style={{ borderColor: "#C084FC40" }}
        />
      </div>
    </div>
  );
};

// ─── Step constants ───────────────────────────────────────────────────────────

const STEPS = [
  { label: "Birth Details", icon: "✦" },
  { label: "Relationship", icon: "◈" },
  { label: "Family", icon: "◉" },
  { label: "Timing", icon: "☽" },
  { label: "Emotions", icon: "✧" },
  { label: "Wedding", icon: "◇" },
  { label: "Review", icon: "⊕" },
];

// ─── Main Component ───────────────────────────────────────────────────────────

const MarriageVerdictForm: React.FC = () => {
  useMvLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormData>(initialForm());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verdictId, setVerdictId] = useState<string | null>(null);

  // If a verdict already exists in this session, skip straight to the result
  // page instead of the form — unless the user explicitly clicked Edit
  // Details (flagged via router state), in which case they want the form.
  useEffect(() => {
    const editIntent = (location.state as { editIntent?: boolean } | null)?.editIntent;
    if (editIntent) return;
    const raw = sessionStorage.getItem("marriageVerdict");
    if (!raw) return;
    try {
      const saved = JSON.parse(raw);
      if (saved?.verdict) {
        navigate("/marriage-verdict/result", { replace: true });
      }
    } catch {
      // Corrupt session data — continue with the normal form flow.
    }
  }, [location.state, navigate]);

  // Hydrate from a previously saved verdict (Edit Details flow) — falls back
  // to a blank form on any missing/corrupt session data.
  useEffect(() => {
    const raw = sessionStorage.getItem("marriageVerdict");
    if (!raw) return;
    try {
      const saved = JSON.parse(raw);
      if (!saved?.formInput) return;
      const { partner_a, partner_b, ...rest } = saved.formInput;
      setForm({
        ...rest,
        partner_a: { ...partner_a, date_of_birth: isoToDob(partner_a.date_of_birth) },
        partner_b: { ...partner_b, date_of_birth: isoToDob(partner_b.date_of_birth) },
      });
      setVerdictId(saved.verdictId || null);
    } catch {
      // Corrupt session data — keep the blank form.
    }
  }, []);

  const setPartner = (
    which: "partner_a" | "partner_b",
    field: keyof PartnerData,
    value: string,
  ) => {
    setForm((prev) => ({
      ...prev,
      [which]: { ...prev[which], [field]: value },
    }));
  };

  const toggleArrayValue = (field: keyof FormData, value: string) => {
    setForm((prev) => {
      const current = (prev[field] as string[]) || [];
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [field]: updated };
    });
  };

  const setField = (field: keyof FormData, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validateStep = (): string | null => {
    if (step === 0) {
      if (!form.partner_a.full_name.trim()) return tx("Partner A name is required");
      if (!form.partner_a.date_of_birth)
        return tx("Partner A date of birth is required");
      if (!form.partner_b.full_name.trim()) return tx("Partner B name is required");
      if (!form.partner_b.date_of_birth)
        return tx("Partner B date of birth is required");
    }
    return null;
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    if (step < STEPS.length - 1) setStep(step + 1);
  };

  const handleBack = () => {
    setError(null);
    if (step > 0) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        ...form,
        partner_a: {
          ...form.partner_a,
          date_of_birth: dobToIso(form.partner_a.date_of_birth),
        },
        partner_b: {
          ...form.partner_b,
          date_of_birth: dobToIso(form.partner_b.date_of_birth),
        },
        userId: localStorage.getItem("userId") || undefined,
      };
      // Edit Details resubmits to the same record (PUT); a first-time
      // submission creates a new one (POST) — both return the same shape.
      const response = verdictId
        ? await api.put(`/backend/marriage-verdict/${verdictId}`, payload)
        : await api.post("/backend/marriage-verdict/generate", payload);
      if (response.data?.success) {
        sessionStorage.setItem(
          "marriageVerdict",
          JSON.stringify({ ...response.data, formInput: payload }),
        );
        navigate("/marriage-verdict/result");
      } else {
        setLoading(false);
        const errs: string[] = response.data?.errors;
        setError(
          Array.isArray(errs) && errs.length > 0
            ? errs.join(" · ")
            : response.data?.message ||
                tx("Failed to generate verdict. Please try again."),
        );
      }
    } catch (err: any) {
      setLoading(false);
      const errs: string[] = err?.response?.data?.errors;
      setError(
        Array.isArray(errs) && errs.length > 0
          ? errs.join(" · ")
          : err?.response?.data?.message ||
              tx("Something went wrong. Please try again."),
      );
    }
  };

  if (loading) return <LoadingScreen />;

  const renderStep = () => {
    switch (step) {
      case 0:
        return <StepBirthDetails form={form} setPartner={setPartner} />;
      case 1:
        return (
          <StepRelationship
            form={form}
            setField={setField}
            toggleArray={toggleArrayValue}
          />
        );
      case 2:
        return (
          <StepFamilyCulture
            form={form}
            setField={setField}
            toggleArray={toggleArrayValue}
          />
        );
      case 3:
        return (
          <StepMarriageTiming
            form={form}
            setField={setField}
            toggleArray={toggleArrayValue}
          />
        );
      case 4:
        return <StepEmotionalPattern form={form} setField={setField} />;
      case 5:
        return (
          <StepWeddingPreferences
            form={form}
            setField={setField}
            toggleArray={toggleArrayValue}
          />
        );
      case 6:
        return <StepReview form={form} onEdit={setStep} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0f0a14] [color-scheme:dark]">
      <div className="h-1 w-full" />

      <div className="min-h-screen w-full px-3 sm:px-4 py-10 sm:py-12">
        <div className="w-full max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-3">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#C084FC" }}
              />
              <span
                className="text-[10px] tracking-[0.25em] uppercase"
                style={{ color: "#a89bb5" }}
              >
                {tx("Vedic Marriage Engine")}
              </span>
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#C084FC" }}
              />
            </div>
            <h1
              className="text-2xl sm:text-3xl font-light tracking-wide"
              style={{ color: "#c8bfd1" }}
            >
              {tx("Marriage Verdict")}
            </h1>
            <p className="text-xs sm:text-sm mt-2" style={{ color: "#a89bb5" }}>
              {tx("Cosmic alignment · Guna Milan · Vivah Muhurat")}
            </p>
          </div>

          {/* Step pills */}
          <div className="flex items-center justify-center gap-1 sm:gap-2 mb-6 flex-wrap">
            {STEPS.map((s, i) => (
              <button
                key={i}
                onClick={() => i < step && setStep(i)}
                className="flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-[10px] sm:text-xs font-semibold tracking-wide border transition-all duration-200"
                style={
                  i === step
                    ? {
                        background: "#a78bff",
                        borderColor: "#C084FC",
                        color: "#0f0a14",
                        boxShadow: "0 2px 8px #C084FC44",
                      }
                    : i < step
                      ? {
                          background: "#221a29",
                          borderColor: "#C084FC",
                          color: "#d9ccff",
                          cursor: "pointer",
                        }
                      : {
                          background: "#1a131f",
                          borderColor: "rgba(255,255,255,0.12)",
                          color: "#a89bb5",
                          cursor: "default",
                        }
                }
              >
                {i < step ? (
                  <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                    <path
                      d="M1.5 4.5l2 2 4-4"
                      stroke="#d9ccff"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <span>{s.icon}</span>
                )}
                <span className="hidden sm:inline">{tx(s.label)}</span>
                <span className="sm:hidden">{i + 1}</span>
              </button>
            ))}
          </div>

          {/* Progress bar */}
          <div
            className="h-1 rounded-full mb-8 overflow-hidden"
            style={{ background: "rgba(255,255,255,0.1)" }}
          >
            <motion.div
              className="h-full rounded-full"
              style={{
                background: "#a78bff", color: "#0f0a14", boxShadow: "0 0 22px rgba(167,139,255,0.35)", borderRadius: 14,
              }}
              initial={{ width: 0 }}
              animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
            />
          </div>

          {/* Step card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="rounded-3xl p-6 sm:p-8 shadow-sm border"
              style={{
                background: "#1a131f",
                borderColor: "rgba(255,255,255,0.1)",
              }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-3 text-red-500 text-xs text-center font-medium"
              >
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex gap-3 mt-6">
            {step > 0 && (
              <button
                onClick={handleBack}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#1a131f] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-[#c8bfd1] shadow-sm transition hover:bg-[#221a29]"
              >
                {tx("← Back")}
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button
                onClick={handleNext}
                className="flex-1 rounded-full py-3 px-8 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:opacity-90"
                style={{
                  background: "#a78bff", color: "#0f0a14", boxShadow: "0 0 22px rgba(167,139,255,0.35)", borderRadius: 14,
                }}
              >
                {tx("Save & Continue →")}
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="flex-1 rounded-full py-3 px-8 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:opacity-90"
                style={{
                  background: "#a78bff", color: "#0f0a14", boxShadow: "0 0 22px rgba(167,139,255,0.35)", borderRadius: 14,
                }}
              >
                {verdictId ? tx("✨ Update Marriage Verdict") : tx("✨ Generate Marriage Verdict")}
              </button>
            )}
          </div>

          <div
            className="text-center mt-6 text-[10px] sm:text-[11px] tracking-wide"
            style={{ color: "#a89bb5" }}
          >
            <span
              className="inline-block border-t w-12 mx-2 align-middle"
              style={{ borderColor: "#C084FC40" }}
            />
            {tx("nakshatra · guna milan · vivah muhurat · vastu")}
            <span
              className="inline-block border-t w-12 mx-2 align-middle"
              style={{ borderColor: "#C084FC40" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Step 1: Birth Details ────────────────────────────────────────────────────

const StepBirthDetails: React.FC<{
  form: FormData;
  setPartner: (
    which: "partner_a" | "partner_b",
    field: keyof PartnerData,
    value: string,
  ) => void;
}> = ({ form, setPartner }) => (
  <div>
    <div className="pb-3 mb-6" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
      <h2
        className="text-lg font-light tracking-wide"
        style={{ color: "#c8bfd1" }}
      >
        {tx("Birth Details")}
      </h2>
      <p className="text-xs mt-1" style={{ color: "#a89bb5" }}>
        {tx("Your birth details anchor your cosmic pattern.")}
      </p>
    </div>
    <div className="space-y-8">
      {(["partner_a", "partner_b"] as const).map((which, idx) => (
        <div key={which}>
          <div className="flex items-center gap-2 mb-4">
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
              style={{
                background:
                  idx === 0
                    ? "linear-gradient(135deg,#C084FC,#6D4C92)"
                    : "linear-gradient(135deg,#C084FC,#6D4C92)",
              }}
            >
              {idx === 0 ? "A" : "B"}
            </span>
            <h3
              className="text-xs font-semibold tracking-[0.15em] uppercase"
              style={{ color: "#c8bfd1" }}
            >
              {tx("Partner")} {idx === 0 ? "A" : "B"}
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField
              label="Full Name"
              value={form[which].full_name}
              onChange={(v) => setPartner(which, "full_name", v)}
              placeholder="Enter full name"
              required
            />
            <DateOfBirthField
              value={form[which].date_of_birth}
              onChange={(v) => setPartner(which, "date_of_birth", v)}
              required
            />
            <TimeOfBirthField
              value={form[which].time_of_birth}
              onChange={(v) => setPartner(which, "time_of_birth", v)}
            />
            <InputField
              label="Place of Birth"
              value={form[which].place_of_birth}
              onChange={(v) => setPartner(which, "place_of_birth", v)}
              placeholder="City, Country"
            />
            <InputField
              label="Current City"
              value={form[which].current_city}
              onChange={(v) => setPartner(which, "current_city", v)}
              placeholder="Current city"
            />
            <div>
              <label
                className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-1.5"
                style={{ color: "#c8bfd1" }}
              >
                {tx("Gender")}
              </label>
              <div className="flex flex-wrap gap-2">
                {["Male", "Female", "Non-binary"].map((g) => (
                  <PillOption
                    key={g}
                    label={g}
                    selected={form[which].gender_expression === g}
                    onClick={() => setPartner(which, "gender_expression", g)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ─── Step 2: Relationship ─────────────────────────────────────────────────────

const StepRelationship: React.FC<{
  form: FormData;
  setField: (f: keyof FormData, v: any) => void;
  toggleArray: (f: keyof FormData, v: string) => void;
}> = ({ form, setField }) => (
  <div>
    <div className="pb-3 mb-6" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
      <h2 className="text-lg font-light" style={{ color: "#c8bfd1" }}>
        {tx("Relationship Context")}
      </h2>
      <p className="text-xs mt-1" style={{ color: "#a89bb5" }}>
        {tx("Every relationship has a rhythm. Let Astria read yours.")}
      </p>
    </div>
    <div className="space-y-6">
      <div>
        <label
          className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
          style={{ color: "#c8bfd1" }}
        >
          {tx("Relationship Stage")}
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {[
            { val: "talking", label: "Talking" },
            { val: "dating", label: "Dating" },
            { val: "committed", label: "Committed" },
            { val: "engaged", label: "Engaged" },
            { val: "arranged", label: "Arranged" },
            { val: "long_distance", label: "Long Distance" },
          ].map(({ val, label }) => (
            <CardOption
              key={val}
              label={tx(label)}
              selected={form.relationship_stage === val}
              onClick={() => setField("relationship_stage", val)}
            />
          ))}
        </div>
      </div>
      <div>
        <label
          className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
          style={{ color: "#c8bfd1" }}
        >
          {tx("Current Feeling")}
        </label>
        <div className="flex flex-wrap gap-2">
          {["Stable", "Intense", "Uncertain", "Emotional", "Peaceful"].map(
            (v) => (
              <PillOption
                key={v}
                label={v}
                selected={form.current_feeling === v.toLowerCase()}
                onClick={() => setField("current_feeling", v.toLowerCase())}
              />
            ),
          )}
        </div>
      </div>
      <InputField
        label="Time Together"
        value={form.time_together}
        onChange={(v) => setField("time_together", v)}
        placeholder="e.g. 2 years, 6 months"
      />
      <div>
        <label
          className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
          style={{ color: "#c8bfd1" }}
        >
          {tx("Previous Breakup?")}
        </label>
        <div className="flex gap-3">
          {["Yes", "No"].map((v) => (
            <PillOption
              key={v}
              label={v}
              selected={form.broken_up_before === v.toLowerCase()}
              onClick={() => setField("broken_up_before", v.toLowerCase())}
            />
          ))}
        </div>
      </div>
      <div>
        <label
          className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
          style={{ color: "#c8bfd1" }}
        >
          {tx("Who Initiated?")}
        </label>
        <div className="flex flex-wrap gap-2">
          {["Partner A", "Partner B", "Mutual"].map((v) => (
            <PillOption
              key={v}
              label={v}
              selected={form.initiated_by === v}
              onClick={() => setField("initiated_by", v)}
            />
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ─── Step 3: Family & Culture ─────────────────────────────────────────────────

const StepFamilyCulture: React.FC<{
  form: FormData;
  setField: (f: keyof FormData, v: any) => void;
  toggleArray: (f: keyof FormData, v: string) => void;
}> = ({ form, setField, toggleArray }) => (
  <div>
    <div className="pb-3 mb-6" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
      <h2 className="text-lg font-light" style={{ color: "#c8bfd1" }}>
        {tx("Family & Culture")}
      </h2>
      <p className="text-xs mt-1" style={{ color: "#a89bb5" }}>
        {tx("In India, marriage is a union of two families.")}
      </p>
    </div>
    <div className="space-y-6">
      <div>
        <label
          className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
          style={{ color: "#c8bfd1" }}
        >
          {tx("Family Support Level")}
        </label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { val: "full", label: "Fully Supportive" },
            { val: "partial", label: "Partly Supportive" },
            { val: "neutral", label: "Neutral" },
            { val: "resistant", label: "Resistant" },
          ].map(({ val, label }) => (
            <CardOption
              key={val}
              label={tx(label)}
              selected={form.family_support === val}
              onClick={() => setField("family_support", val)}
            />
          ))}
        </div>
      </div>
      <div>
        <label
          className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
          style={{ color: "#c8bfd1" }}
        >
          {tx("Religion / Culture")}
        </label>
        <div className="flex flex-wrap gap-2">
          {["Hindu", "Jain", "Sikh", "Muslim", "Christian", "Mixed"].map(
            (v) => (
              <PillOption
                key={v}
                label={v}
                selected={form.religion_culture.includes(v)}
                onClick={() => toggleArray("religion_culture", v)}
              />
            ),
          )}
        </div>
      </div>
      <InputField
        label="Language Background"
        value={form.language_background}
        onChange={(v) => setField("language_background", v)}
        placeholder="e.g. Hindi, Tamil, Punjabi"
      />
    </div>
  </div>
);

// ─── Step 4: Marriage Timing ──────────────────────────────────────────────────

const StepMarriageTiming: React.FC<{
  form: FormData;
  setField: (f: keyof FormData, v: any) => void;
  toggleArray: (f: keyof FormData, v: string) => void;
}> = ({ form, setField, toggleArray }) => (
  <div>
    <div className="pb-3 mb-6" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
      <h2 className="text-lg font-light" style={{ color: "#c8bfd1" }}>
        {tx("Marriage Timing")}
      </h2>
      <p className="text-xs mt-1" style={{ color: "#a89bb5" }}>
        {tx("Your intention shapes the timing. Timing shapes the path.")}
      </p>
    </div>
    <div className="space-y-6">
      <div>
        <label
          className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
          style={{ color: "#c8bfd1" }}
        >
          {tx("When do you plan to marry?")}
        </label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { val: "within_6_months", label: "Within 6 months" },
            { val: "within_1_year", label: "Within 1 year" },
            { val: "no_fixed_plan", label: "No fixed plan" },
            { val: "family_decided", label: "Family decided" },
          ].map(({ val, label }) => (
            <CardOption
              key={val}
              label={tx(label)}
              selected={form.plan_to_marry === val}
              onClick={() => setField("plan_to_marry", val)}
            />
          ))}
        </div>
      </div>
      <div>
        <label
          className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
          style={{ color: "#c8bfd1" }}
        >
          {tx("Preferred Wedding Days")}
        </label>
        <div className="flex flex-wrap gap-2">
          {["Weekend", "Weekday", "Morning", "Evening", "Flexible"].map((v) => (
            <PillOption
              key={v}
              label={v}
              selected={form.preferred_day_type.includes(v.toLowerCase())}
              onClick={() => toggleArray("preferred_day_type", v.toLowerCase())}
            />
          ))}
        </div>
      </div>
      <div>
        <label
          className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
          style={{ color: "#c8bfd1" }}
        >
          {tx("Wedding Style")}
        </label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { val: "intimate", label: "Intimate" },
            { val: "traditional", label: "Traditional" },
            { val: "modern", label: "Modern" },
            { val: "big_family", label: "Big Family Wedding" },
          ].map(({ val, label }) => (
            <CardOption
              key={val}
              label={tx(label)}
              selected={form.wedding_style === val}
              onClick={() => setField("wedding_style", val)}
            />
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ─── Step 5: Emotional Pattern ────────────────────────────────────────────────

const StepEmotionalPattern: React.FC<{
  form: FormData;
  setField: (f: keyof FormData, v: any) => void;
}> = ({ form, setField }) => (
  <div>
    <div className="pb-3 mb-6" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
      <h2 className="text-lg font-light" style={{ color: "#c8bfd1" }}>
        {tx("Emotional Pattern")}
      </h2>
      <p className="text-xs mt-1" style={{ color: "#a89bb5" }}>
        {tx("Your emotional pattern reveals how your charts interact.")}
      </p>
    </div>
    <div className="space-y-6">
      <div>
        <label
          className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
          style={{ color: "#c8bfd1" }}
        >
          This relationship feels like…
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {[
            { val: "grounding", label: "Grounding" },
            { val: "karmic", label: "Karmic" },
            { val: "intense", label: "Intense" },
            { val: "healing", label: "Healing" },
            { val: "destiny_like", label: "Destiny-like" },
            { val: "uncertain", label: "Uncertain" },
          ].map(({ val, label }) => (
            <CardOption
              key={val}
              label={tx(label)}
              selected={form.relationship_feels_like === val}
              onClick={() => setField("relationship_feels_like", val)}
            />
          ))}
        </div>
      </div>
      <div>
        <label
          className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
          style={{ color: "#c8bfd1" }}
        >
          {tx("Biggest Challenge")}
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {[
            { val: "communication", label: "Communication" },
            { val: "family", label: "Family Expectations" },
            { val: "money", label: "Money" },
            { val: "distance", label: "Distance" },
            { val: "emotional_rhythm", label: "Emotional Rhythm" },
            { val: "trust", label: "Trust" },
          ].map(({ val, label }) => (
            <CardOption
              key={val}
              label={tx(label)}
              selected={form.biggest_challenge === val}
              onClick={() => setField("biggest_challenge", val)}
            />
          ))}
        </div>
      </div>
      <div>
        <label
          className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
          style={{ color: "#c8bfd1" }}
        >
          {tx("Strongest Point")}
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {[
            { val: "support", label: "Support" },
            { val: "attraction", label: "Attraction" },
            { val: "shared_goals", label: "Shared Goals" },
            { val: "stability", label: "Stability" },
            { val: "spiritual_connection", label: "Spiritual Connection" },
          ].map(({ val, label }) => (
            <CardOption
              key={val}
              label={tx(label)}
              selected={form.strongest_point === val}
              onClick={() => setField("strongest_point", val)}
            />
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ─── Step 6: Wedding Preferences ─────────────────────────────────────────────

const COMMON_COLORS = [
  "Red",
  "Gold",
  "Cream",
  "Pink",
  "Orange",
  "White",
  "Green",
  "Blue",
  "Purple",
  "Yellow",
];

const StepWeddingPreferences: React.FC<{
  form: FormData;
  setField: (f: keyof FormData, v: any) => void;
  toggleArray: (f: keyof FormData, v: string) => void;
}> = ({ form, setField, toggleArray }) => (
  <div>
    <div className="pb-3 mb-6" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
      <h2 className="text-lg font-light" style={{ color: "#c8bfd1" }}>
        {tx("Wedding Preferences")}
      </h2>
      <p className="text-xs mt-1" style={{ color: "#a89bb5" }}>
        {tx("Your choices shape the ceremony's energy.")}
      </p>
    </div>
    <div className="space-y-6">
      <div>
        <label
          className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
          style={{ color: "#c8bfd1" }}
        >
          {tx("Venue Type")}
        </label>
        <div className="flex gap-3">
          {["Indoor", "Outdoor"].map((v) => (
            <PillOption
              key={v}
              label={v}
              selected={form.venue_type === v.toLowerCase()}
              onClick={() => setField("venue_type", v.toLowerCase())}
            />
          ))}
        </div>
      </div>
      <div>
        <label
          className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
          style={{ color: "#c8bfd1" }}
        >
          {tx("Preferred Colors")}
        </label>
        <div className="flex flex-wrap gap-2">
          {COMMON_COLORS.map((c) => (
            <PillOption
              key={c}
              label={c}
              selected={form.preferred_colors.includes(c)}
              onClick={() => toggleArray("preferred_colors", c)}
            />
          ))}
        </div>
      </div>
      <div>
        <label
          className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
          style={{ color: "#c8bfd1" }}
        >
          {tx("Colors to Avoid")}
        </label>
        <div className="flex flex-wrap gap-2">
          {COMMON_COLORS.map((c) => (
            <PillOption
              key={c}
              label={c}
              selected={form.colors_to_avoid.includes(c)}
              onClick={() => toggleArray("colors_to_avoid", c)}
            />
          ))}
        </div>
      </div>
      <div>
        <label
          className="block text-[10px] font-semibold tracking-[0.15em] uppercase mb-2"
          style={{ color: "#c8bfd1" }}
        >
          {tx("Wedding Style Preference")}
        </label>
        <div className="flex flex-wrap gap-2">
          {["Traditional", "Modern", "Fusion"].map((v) => (
            <PillOption
              key={v}
              label={v}
              selected={form.wedding_style_preference === v}
              onClick={() => setField("wedding_style_preference", v)}
            />
          ))}
        </div>
      </div>
      <div className="space-y-3">
        <ToggleSwitch
          label="Include Gift Oracle"
          description="Personalized gifts aligned with your birth energies"
          value={form.need_gift_list}
          onChange={(v) => setField("need_gift_list", v)}
        />
        <ToggleSwitch
          label="Include Wedding Checklist"
          description="Cosmic-timed preparation timeline"
          value={form.need_checklist}
          onChange={(v) => setField("need_checklist", v)}
        />
        <ToggleSwitch
          label="Vastu-Aligned Direction"
          description="Auspicious spatial placement for your ceremony"
          value={form.need_vastu_direction}
          onChange={(v) => setField("need_vastu_direction", v)}
        />
      </div>
    </div>
  </div>
);

// ─── Step 7: Review ───────────────────────────────────────────────────────────

const ReviewRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div
    className="flex justify-between py-2"
    style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}
  >
    <span className="text-[11px]" style={{ color: "#a89bb5" }}>
      {tx(label)}
    </span>
    <span
      className="text-[11px] font-medium text-right max-w-[60%]"
      style={{ color: "#f7f3fa" }}
    >
      {tx(value) || "—"}
    </span>
  </div>
);

const StepReview: React.FC<{ form: FormData; onEdit: (s: number) => void }> = ({
  form,
  onEdit,
}) => (
  <div>
    <div className="pb-3 mb-6" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
      <h2 className="text-lg font-light" style={{ color: "#c8bfd1" }}>
        {tx("Review")}
      </h2>
      <p className="text-xs mt-1" style={{ color: "#a89bb5" }}>
        {tx("Astria will calculate your compatibility, timing, and cosmic alignment.")}
      </p>
    </div>
    <div className="space-y-4">
      {[
        {
          title: "Birth Details",
          step: 0,
          rows: [
            {
              label: "Partner A",
              value: `${form.partner_a.full_name} — ${form.partner_a.date_of_birth}`,
            },
            {
              label: "Partner B",
              value: `${form.partner_b.full_name} — ${form.partner_b.date_of_birth}`,
            },
          ],
        },
        {
          title: "Relationship",
          step: 1,
          rows: [
            { label: "Stage", value: form.relationship_stage },
            { label: "Feeling", value: form.current_feeling },
            { label: "Time Together", value: form.time_together },
          ],
        },
        {
          title: "Family & Timing",
          step: 2,
          rows: [
            { label: "Family Support", value: ({ full: "Fully Supportive", partial: "Partly Supportive", neutral: "Neutral", resistant: "Resistant" } as Record<string, string>)[form.family_support] ?? form.family_support },
            { label: "Religion", value: form.religion_culture.join(", ") },
            { label: "Plan to Marry", value: form.plan_to_marry },
          ],
        },
        {
          title: "Add-ons",
          step: 5,
          rows: [
            { label: "Gift Oracle", value: form.need_gift_list ? "Yes" : "No" },
            { label: "Checklist", value: form.need_checklist ? "Yes" : "No" },
            {
              label: "Vastu Guidance",
              value: form.need_vastu_direction ? "Yes" : "No",
            },
          ],
        },
      ].map(({ title, step: s, rows }) => (
        <div
          key={title}
          className="rounded-2xl p-4 border"
          style={{ background: "#221a29", borderColor: "rgba(255,255,255,0.1)" }}
        >
          <div className="flex justify-between items-center mb-2">
            <h3
              className="text-[10px] font-semibold tracking-[0.15em] uppercase"
              style={{ color: "#C084FC" }}
            >
              {tx(title)}
            </h3>
            <button
              onClick={() => onEdit(s)}
              className="text-[10px] font-semibold uppercase tracking-wide transition-colors"
              style={{ color: "#d9ccff" }}
            >
              {tx("Edit")}
            </button>
          </div>
          {rows.map((r) => (
            <ReviewRow key={r.label} label={r.label} value={r.value} />
          ))}
        </div>
      ))}
    </div>
  </div>
);

export default MarriageVerdictForm;
